import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption, SyutTreeSelect, type TreeSelectOption } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useAuthContext } from 'contexts/AuthContext';
import {
  accountTypeService, reportClassService, accountSubtypeService, coaGroupService, coaLedgerService,
  entityAccountingConfigService, currencyService,
} from 'api/services';
import type { CoaLedgerDto, AccountSubtypeDto } from 'api/types';

// ============================================================================
// ACCOUNT LEDGER MODALS — reference-parity with the sample/Account Group modals.
// Form modal (create / edit / copy + minimize + concurrency) and View modal.
// Manual creation always produces a GL ledger; PARTY ledgers are auto-provisioned.
// ============================================================================

export interface AccountLedgerFormData {
  accountTypeId: string;
  reportClassId: string;
  accountSubtypeId: string;
  coaGroupId: string;
  ledgerCode: string;
  ledgerName: string;
  currencyCode: string;
  ledgerNature: string;
  isOpenItemManaged: boolean;
  isReconcilable: boolean;
  moduleCodes: string[];
  description: string;
  statusCode: string;
}

const INITIAL_FORM_DATA: AccountLedgerFormData = {
  accountTypeId: '',
  reportClassId: '',
  accountSubtypeId: '',
  coaGroupId: '',
  ledgerCode: '',
  ledgerName: '',
  // Empty on new records — the form's mount-effect resolves the entity's
  // functional currency from EntityAccountingConfig and stamps it here.
  currencyCode: '',
  ledgerNature: 'GENERAL',
  isOpenItemManaged: false,
  isReconcilable: false,
  moduleCodes: [],
  description: '',
  statusCode: 'ACTIVE',
};

/** ADR-0061 structured GL code: type · class · subtype (2) · serial (3). */
const STRUCTURED_CODE = /^[0-9]{7}$/;

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

function buildFormData(l: CoaLedgerDto | null | undefined, mode: 'create' | 'edit' | 'copy'): AccountLedgerFormData {
  if ((mode === 'edit' || mode === 'copy') && l) {
    return {
      accountTypeId: l.accountTypeId,
      reportClassId: l.reportClassId ?? '',
      accountSubtypeId: l.accountSubtypeId,
      coaGroupId: l.coaGroupId ?? '',
      ledgerCode: mode === 'copy' ? '' : l.ledgerCode,
      ledgerName: mode === 'copy' ? '' : l.ledgerName,
      currencyCode: l.currencyCode || '',
      ledgerNature: l.ledgerNature || 'GENERAL',
      isOpenItemManaged: l.isOpenItemManaged,
      isReconcilable: l.isReconcilable,
      moduleCodes: [...(l.moduleTags ?? l.moduleCodesList ?? [])],
      description: l.description ?? '',
      // Empty → backend defaults to ACTIVE via IStatusCache.
      statusCode: mode === 'copy' ? '' : (l.statusCode || ''),
    };
  }
  return { ...INITIAL_FORM_DATA };
}

// ============================================================================
// FORM MODAL
// ============================================================================

export interface AccountLedgerFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  ledger?: CoaLedgerDto | null;
  onSave: () => void;
  onClose: () => void;
  openedBy?: string;
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, ledgerCode?: string) => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

export const AccountLedgerFormModal: React.FC<AccountLedgerFormModalProps> = ({
  isOpen, mode, ledger, onSave, onClose, openedBy,
  tabId, moduleName, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<AccountLedgerFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const ledgerRef = useRef(ledger);
  ledgerRef.current = ledger;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const justRestoredRef = useRef(false);   // this open was a restore from the minimize bar
  const functionalCurrencyRef = useRef<string | null>(null);   // entity's functional currency, cached across opens
  const currencyLabelCache = useRef<Map<string, string>>(new Map());

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // ADR-0027 ③c — "Available to branches" whitelist (edit mode only: the PUT
  // needs a ledger id). Empty selection = available to all branches.
  const { accessibleBusinessUnits, selectedEntity } = useAuthContext();
  const entityBuOptions: SelectOption[] = useMemo(() => {
    const list = selectedEntity
      ? accessibleBusinessUnits.filter(b => b.entityId === selectedEntity.id)
      : accessibleBusinessUnits;
    return list.map(b => ({ value: b.id, label: `${b.buCode} - ${b.buName}` }));
  }, [accessibleBusinessUnits, selectedEntity]);
  const [buAccess, setBuAccess] = useState<string[]>([]);
  const [buAccessLoaded, setBuAccessLoaded] = useState(false);
  const initialBuAccessRef = useRef('[]');

  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !ledger?.id) {
      setBuAccess([]); setBuAccessLoaded(false); initialBuAccessRef.current = '[]';
      return;
    }
    let cancelled = false;
    setBuAccessLoaded(false);
    coaLedgerService.getBuAccess(ledger.id)
      .then(ids => {
        if (cancelled) return;
        setBuAccess(ids);
        initialBuAccessRef.current = JSON.stringify([...ids].sort());
        setBuAccessLoaded(true);
      })
      .catch(() => { /* stays not-loaded → save skips the PUT so a failed GET can't clobber the whitelist */ });
    return () => { cancelled = true; };
  }, [isOpen, mode, ledger?.id]);

  const buAccessDirty = buAccessLoaded && JSON.stringify([...buAccess].sort()) !== initialBuAccessRef.current;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    justRestoredRef.current = isRestoreRef.current;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const data = buildFormData(ledgerRef.current, modeRef.current);
    // New ledgers default to the entity's functional currency (use the cached value if we have it).
    if (modeRef.current === 'create' && functionalCurrencyRef.current) data.currencyCode = functionalCurrencyRef.current;
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});

    // First create-open: fetch the functional currency, then apply it while the form is still pristine.
    if (modeRef.current === 'create' && !functionalCurrencyRef.current) {
      entityAccountingConfigService.getConfig().then(c => {
        const fc = (c.functionalCurrencyCode || '').trim().toUpperCase();
        if (!fc) return;
        functionalCurrencyRef.current = fc;
        setFormData(prev => {
          if (JSON.stringify(prev) !== initialSnapshotRef.current) return prev;   // user already edited → don't clobber
          const next = { ...prev, currencyCode: fc };
          initialSnapshotRef.current = JSON.stringify(next);                       // re-baseline so it isn't "dirty"
          return next;
        });
      }).catch(() => { /* keep INR fallback */ });
    }
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => { if (isDirty || buAccessDirty) setDiscardConfirmOpen(true); else onClose(); }, [isDirty, buAccessDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const fd = formDataRef.current;
    const m = modeRef.current;
    const cId = createInstanceIdRef.current;
    const pId = copyInstanceIdRef.current;

    let modalId: string;
    if (m === 'create') modalId = `${tabId}-form-modal-create-${cId || 'default'}`;
    else if (m === 'copy') modalId = `${tabId}-form-modal-copy-${pId || 'default'}`;
    else modalId = `${tabId}-form-modal-edit-${fd.ledgerCode || 'new'}`;

    const code = fd.ledgerCode?.trim();
    const name = fd.ledgerName?.trim();
    let title: string;
    if (m === 'create') { const i = cId || '1'; title = code ? `Add - ${code} (${i})` : name ? `Add - ${name} (${i})` : `Add New (${i})`; }
    else if (m === 'copy') { const i = pId || '1'; title = code ? `Copy - ${code} (${i})` : name ? `Copy - ${name} (${i})` : `Copy (${i})`; }
    else title = `Edit - ${fd.ledgerCode || 'Ledger'}`;

    const savedFormData = { ...fd };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(fd) !== savedSnapshot : false;

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          m,
          m === 'create' ? cId : m === 'copy' ? pId : undefined,
          m !== 'create' ? fd.ledgerCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const getFieldError = (f: string) => fieldErrors[f];
  const clearFieldError = (f: string) => setFieldErrors(prev => { if (!prev[f]) return prev; const n = { ...prev }; delete n[f]; return n; });

  // ------------------------------------------------------------------
  // Account category tree (Type › Report Class › Subtype). One picker replaces
  // the three cascading selects: a ledger is placed on a SUBTYPE, and its type
  // and report class follow from it. Type/class rows are headings only.
  // The subtype list is already narrowed to the entity's country / legal form.
  // ------------------------------------------------------------------
  const [categoryTree, setCategoryTree] = useState<TreeSelectOption[]>([]);
  const [treeLoaded, setTreeLoaded] = useState(false);
  const subtypeMapRef = useRef<Map<string, AccountSubtypeDto>>(new Map());
  const typeNameRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!isOpen || treeLoaded) return;
    let cancelled = false;
    Promise.all([
      accountTypeService.getAll({ pageSize: 50 }),
      reportClassService.getAll({ pageSize: 100 }),
      accountSubtypeService.getAll({ pageSize: 100 }),
    ]).then(([types, classes, subtypes]) => {
      if (cancelled) return;
      subtypeMapRef.current = new Map(subtypes.data.map(s => [s.id, s]));
      typeNameRef.current = new Map(types.data.map(t => [t.id, t.typeName]));
      // Only headings that end in at least one subtype.
      const usedClasses = new Set(subtypes.data.map(s => s.reportClassId).filter(Boolean) as string[]);
      const usedTypes = new Set(subtypes.data.map(s => s.accountTypeId));
      const opts: TreeSelectOption[] = [
        ...types.data.filter(t => usedTypes.has(t.id)).map(t => ({
          value: `t:${t.id}`, label: t.typeName, isCategory: true, sortOrder: t.displayOrder,
        })),
        ...classes.data.filter(rc => usedClasses.has(rc.id)).map(rc => ({
          value: `c:${rc.id}`, label: rc.className, parentValue: `t:${rc.accountTypeId}`, isCategory: true, sortOrder: rc.displayOrder,
        })),
        ...subtypes.data.map(s => ({
          value: s.id,
          label: `${s.subtypeName} (${s.normalBalance})`,
          parentValue: s.reportClassId ? `c:${s.reportClassId}` : `t:${s.accountTypeId}`,
          sortOrder: s.displayOrder,
        })),
      ];
      setCategoryTree(opts);
      setTreeLoaded(true);
    }).catch(() => notificationService.toast.error('Could not load the account categories.'));
    return () => { cancelled = true; };
  }, [isOpen, treeLoaded]);

  // "Asset › Current Assets" under the picker, so the full placement stays visible.
  const categoryPath = useMemo(() => {
    const s = formData.accountSubtypeId ? subtypeMapRef.current.get(formData.accountSubtypeId) : undefined;
    if (!s) return ledger && formData.accountSubtypeId === ledger.accountSubtypeId
      ? [ledger.accountTypeName, ledger.reportClassName].filter(Boolean).join(' › ')
      : '';
    return [s.accountTypeName || typeNameRef.current.get(s.accountTypeId), s.reportClassName].filter(Boolean).join(' › ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.accountSubtypeId, treeLoaded, ledger]);

  // Open the tree at the current subtype (edit / copy / restore).
  const expandedForValue = useMemo(() => {
    const s = formData.accountSubtypeId ? subtypeMapRef.current.get(formData.accountSubtypeId) : undefined;
    if (!s) return [];
    return [`t:${s.accountTypeId}`, ...(s.reportClassId ? [`c:${s.reportClassId}`] : [])];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeLoaded]);

  // ------------------------------------------------------------------
  // Auto code (ADR-0061): the next structured code in the subtype's block.
  // Filled only while the code is empty or still the last suggestion, so a
  // code the user typed is never overwritten. Edit mode never re-codes.
  // ------------------------------------------------------------------
  const lastSuggestedRef = useRef('');
  const [suggesting, setSuggesting] = useState(false);
  const [noAutoCode, setNoAutoCode] = useState(false);

  const suggestCode = useCallback(async (subtypeId: string, force = false) => {
    if (modeRef.current === 'edit' || !subtypeId) return;
    const current = formDataRef.current.ledgerCode;
    if (!force && current && current !== lastSuggestedRef.current) return;
    setSuggesting(true);
    try {
      const code = await coaLedgerService.getNextCode(subtypeId);
      if (formDataRef.current.accountSubtypeId !== subtypeId) return;   // user moved on
      setNoAutoCode(!code);
      const next = code ?? '';
      setFormData(prev => {
        if (!force && prev.ledgerCode && prev.ledgerCode !== lastSuggestedRef.current) return prev;
        return { ...prev, ledgerCode: next };
      });
      lastSuggestedRef.current = next;
      if (code) clearFieldError('ledgerCode');
    } catch {
      /* no suggestion — the user can type a code, or leave it blank for the server to assign */
    } finally {
      setSuggesting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Copy opens with the source's subtype and an empty code — suggest straight away.
  useEffect(() => {
    if (!isOpen) return;
    lastSuggestedRef.current = '';
    setNoAutoCode(false);
    // formDataRef is not yet refreshed in this commit; read what the open will show.
    if (justRestoredRef.current || modeRef.current !== 'copy') return;
    const fd = buildFormData(ledgerRef.current, 'copy');
    if (fd.accountSubtypeId) suggestCode(fd.accountSubtypeId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleCategoryChange = useCallback((val: string | string[] | null) => {
    clearFieldError('accountSubtypeId');
    const v = (Array.isArray(val) ? val[0] : val) || '';
    const s = v ? subtypeMapRef.current.get(v) : undefined;
    const prevData = formDataRef.current;
    // A new category means a new code block. Unless the user typed a free-form code of their
    // own, the old code is dropped at once — never left behind while the new one is fetched —
    // so a ledger can't be saved with another category's number. Edit never re-codes.
    const recode = modeRef.current !== 'edit' && prevData.accountSubtypeId !== v
      && (!prevData.ledgerCode || prevData.ledgerCode === lastSuggestedRef.current || STRUCTURED_CODE.test(prevData.ledgerCode));
    const next = {
      ...prevData,
      accountSubtypeId: v,
      accountTypeId: s?.accountTypeId ?? '',
      reportClassId: s?.reportClassId ?? '',
      coaGroupId: prevData.accountSubtypeId === v ? prevData.coaGroupId : '',
      ledgerCode: recode ? '' : prevData.ledgerCode,
    };
    setFormData(next);
    formDataRef.current = next;
    if (recode) {
      lastSuggestedRef.current = '';
      if (v) suggestCode(v, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestCode]);
  const loadGroupOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const subtypeId = formDataRef.current.accountSubtypeId;
    if (!subtypeId) return [];
    const res = await coaGroupService.getAll({ accountSubtypeId: subtypeId, searchTerm: input || undefined, pageSize: 100 });
    return res.data.map(g => ({ value: g.id, label: `${g.groupCode} - ${g.groupName}` }));
  }, []);
  const loadNatureOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const natures = await coaLedgerService.getNatures();
    const filtered = input ? natures.filter(n => n.label.toLowerCase().includes(input.toLowerCase())) : natures;
    return filtered.map(n => ({ value: n.value, label: n.label }));
  }, []);
  const loadCurrencyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const list = await currencyService.getAll(input || undefined);
      list.forEach(c => currencyLabelCache.current.set(c.currencyCode, `${c.currencyCode} — ${c.currencyName}`));
      return list.map(c => ({ value: c.currencyCode, label: `${c.currencyCode} — ${c.currencyName}` }));
    } catch {
      return [];
    }
  }, []);

  // The saved subtype may sit outside this entity's list (e.g. another country's) — seed its label.
  const subtypeSeed: TreeSelectOption[] | undefined = ledger
    ? [{ value: ledger.accountSubtypeId, label: ledger.subtypeName }]
    : undefined;
  const groupSeed = ledger?.coaGroupId ? [{ value: ledger.coaGroupId, label: `${ledger.groupCode ?? ''} - ${ledger.groupName ?? ''}` }] : undefined;
  const natureSeed = ledger ? [{ value: ledger.ledgerNature, label: ledger.ledgerNature }] : [{ value: 'GENERAL', label: 'General' }];

  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!formData.accountSubtypeId) errors.accountSubtypeId = 'Pick the account category (subtype)';
    // A blank code on create is assigned by the server (next in the subtype's block, ADR-0061).
    if (!formData.ledgerCode.trim() && (mode === 'edit' || noAutoCode)) errors.ledgerCode = 'Ledger code is required';
    if (!formData.ledgerName.trim()) errors.ledgerName = 'Ledger name is required';
    if (!formData.currencyCode.trim() || formData.currencyCode.trim().length !== 3) errors.currencyCode = 'Enter a 3-letter ISO currency code';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }

    if (formData.ledgerCode.trim()) {
      try {
        const excludeId = mode === 'edit' ? ledger?.id : undefined;
        const available = await coaLedgerService.isCodeAvailable(formData.ledgerCode.trim(), excludeId);
        if (!available) { setFieldErrors({ ledgerCode: 'This code already exists. Please use a different code.' }); return; }
      } catch { /* let server enforce */ }
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, ledger, noAutoCode]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload = {
        accountSubtypeId: formData.accountSubtypeId,
        coaGroupId: formData.coaGroupId || null,
        ledgerCode: formData.ledgerCode.trim(),
        ledgerName: formData.ledgerName.trim(),
        currencyCode: formData.currencyCode.trim().toUpperCase(),
        ledgerKind: 'GL',
        ledgerNature: formData.ledgerNature || 'GENERAL',
        isOpenItemManaged: formData.isOpenItemManaged,
        isReconcilable: formData.isReconcilable,
        description: formData.description.trim() || undefined,
        statusCode: formData.statusCode,
        moduleCodes: formData.moduleCodes,
      };
      if (mode === 'edit' && ledger) {
        await coaLedgerService.update(ledger.id, { ...payload, rowVersion: ledger.rowVersion });
        // ADR-0027: persist the branch whitelist alongside the ledger save.
        // Skipped unless the GET succeeded (never clobber an unread whitelist).
        if (buAccessLoaded && buAccessDirty) {
          const saved = await coaLedgerService.setBuAccess(ledger.id, buAccess);
          initialBuAccessRef.current = JSON.stringify([...saved].sort());
          setBuAccess(saved);
        }
      } else {
        await coaLedgerService.create(payload);
      }
      setIsSaving(false);
      const label = mode === 'create' ? 'created' : mode === 'copy' ? 'copied' : 'updated';
      notificationService.toast.success(`Ledger "${formData.ledgerName}" has been ${label} successfully.`);
      onSave();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message;
      if (status === 409 && message) setConcurrencyConflict({ open: true, message });
      else notificationService.toast.error(message || 'An error occurred while saving. Please try again.');
    }
  }, [formData, mode, ledger, onSave, onClose]);

  const handleRefreshFromConflict = useCallback(async () => {
    if (!ledger?.id) { setConcurrencyConflict({ open: false, message: '' }); return; }
    setIsRefreshing(true);
    try {
      const res = await coaLedgerService.getById(ledger.id);
      const fresh = buildFormData(res.data, 'edit');
      setFormData(fresh);
      initialSnapshotRef.current = JSON.stringify(fresh);
      setConcurrencyConflict({ open: false, message: '' });
      notificationService.toast.info('Form refreshed with latest data. Please review and save again.');
    } catch {
      notificationService.toast.error('Could not refresh from server. Please close and re-open the record.');
    } finally { setIsRefreshing(false); }
  }, [ledger]);

  if (process.env.NODE_ENV === 'development') {
    console.log('AccountLedgerFormModal opened', { openedBy: openedBy || 'Unknown', mode });
  }

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'create' ? 'Creating ledger...' : mode === 'copy' ? 'Copying ledger...' : 'Saving changes...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' && 'Add Ledger Account'}
            {mode === 'edit' && 'Edit Ledger Account'}
            {mode === 'copy' && 'Copy Ledger Account'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="accountSubtypeId">
              <SyutTreeSelect key={treeLoaded ? 'tree' : 'tree-loading'}
                label={<>Account Category <span className="text-danger">*</span></>}
                options={categoryTree} seedOptions={subtypeSeed}
                value={formData.accountSubtypeId || null}
                onChange={handleCategoryChange}
                defaultExpandedValues={expandedForValue}
                placeholder={treeLoaded ? 'Search or pick a subtype…' : 'Loading categories…'}
                isDisabled={!treeLoaded}
                menuPortalTarget={document.body} maxMenuHeight={360}
                showNodeIcons={false} isClearable={false}
                error={getFieldError('accountSubtypeId')}
                helperText={categoryPath || 'Type › Report class › Subtype'} />
            </div>

            <div className="form-group" data-field="coaGroupId">
              <SyutSelect key={`grp-${formData.accountSubtypeId}`} label="Group (optional)" isAsync loadOptions={loadGroupOptions}
                defaultOptions cacheOptions={false} seedOptions={groupSeed} menuPortalTarget={document.body} value={formData.coaGroupId}
                onChange={(val) => setFormData(prev => ({ ...prev, coaGroupId: (val as string) || '' }))}
                placeholder={formData.accountSubtypeId ? 'Directly under subtype' : 'Pick a category first'} isClearable />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <span>Ledger Code {mode === 'edit' && <span className="text-danger">*</span>}</span>
                {mode !== 'edit' && formData.accountSubtypeId && (
                  <button type="button" className="modal-header-btn" title="Suggest the next code in this category"
                    onClick={() => suggestCode(formData.accountSubtypeId, true)} disabled={suggesting}
                    style={{ padding: '0 0.25rem', height: '1.25rem', marginLeft: 'auto' }}>
                    <IconSystem.refresh size={13} />
                  </button>
                )}
              </label>
              <input type="text" data-field="ledgerCode" className={`form-input${getFieldError('ledgerCode') ? ' form-field-error' : ''}`}
                value={formData.ledgerCode} maxLength={30}
                placeholder={mode === 'edit' ? '' : suggesting ? 'Finding next code…' : noAutoCode ? 'Enter a code' : 'Auto — from the category'}
                onChange={(e) => { clearFieldError('ledgerCode'); setFormData(prev => ({ ...prev, ledgerCode: e.target.value.toUpperCase() })); }}
                readOnly={mode === 'edit'} style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}} />
              {getFieldError('ledgerCode')
                ? <div className="form-error-message">{getFieldError('ledgerCode')}</div>
                : mode !== 'edit' && noAutoCode && formData.accountSubtypeId && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                    No automatic code for this category (its block is full or unnumbered) — enter one.
                  </div>
                )}
            </div>

            <div className="form-group">
              <label className="form-label">Ledger Name <span className="text-danger">*</span></label>
              <input type="text" data-field="ledgerName" className={`form-input${getFieldError('ledgerName') ? ' form-field-error' : ''}`}
                value={formData.ledgerName} maxLength={200} placeholder="e.g., HDFC Bank - Current A/c"
                onChange={(e) => { clearFieldError('ledgerName'); setFormData(prev => ({ ...prev, ledgerName: e.target.value })); }} />
              {getFieldError('ledgerName') && <div className="form-error-message">{getFieldError('ledgerName')}</div>}
            </div>

            <div className="form-group" data-field="ledgerNature">
              <SyutSelect label="Nature" isAsync loadOptions={loadNatureOptions} defaultOptions cacheOptions={false}
                seedOptions={natureSeed} menuPortalTarget={document.body} value={formData.ledgerNature}
                onChange={(val) => setFormData(prev => ({ ...prev, ledgerNature: (val as string) || 'GENERAL' }))} isClearable={false} />
            </div>

            <div className="form-group" data-field="currencyCode">
              <SyutSelect label={<>Base Currency <span className="text-danger">*</span></>}
                isAsync loadOptions={loadCurrencyOptions} defaultOptions cacheOptions={false}
                seedOptions={formData.currencyCode
                  ? [{ value: formData.currencyCode, label: currencyLabelCache.current.get(formData.currencyCode) || formData.currencyCode }]
                  : undefined}
                menuPortalTarget={document.body} value={formData.currencyCode}
                onChange={(val) => { clearFieldError('currencyCode'); setFormData(prev => ({ ...prev, currencyCode: (val as string) || '' })); }}
                isClearable={false} error={getFieldError('currencyCode')} />
            </div>

            <div className="form-group" data-field="statusCode">
              <SyutSelect label="Status" options={STATUS_OPTIONS} menuPortalTarget={document.body} value={formData.statusCode}
                onChange={(val) => setFormData(prev => ({ ...prev, statusCode: (val as string) || '' }))} isClearable={false} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', margin: '0.25rem 0 0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={formData.isOpenItemManaged} onChange={(e) => setFormData(prev => ({ ...prev, isOpenItemManaged: e.target.checked }))} />
              <span className="form-label" style={{ margin: 0 }}>Open-item managed (bill-wise)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={formData.isReconcilable} onChange={(e) => setFormData(prev => ({ ...prev, isReconcilable: e.target.checked }))} />
              <span className="form-label" style={{ margin: 0 }}>Reconcilable</span>
            </label>
          </div>

          {/* ADR-0027 ③c — branch availability whitelist (edit only: needs a ledger id) */}
          {mode === 'edit' ? (
            <div className="form-group" data-field="buAccess">
              <SyutSelect label="Available to branches" isMulti
                options={entityBuOptions}
                menuPortalTarget={document.body}
                value={buAccess}
                onChange={(val) => setBuAccess((val as string[]) || [])}
                placeholder={buAccessLoaded ? 'All branches' : 'Loading…'}
                isDisabled={!buAccessLoaded}
                helperText="Leave empty to keep the ledger available to all branches. Selected branches become a whitelist — transaction pickers in other branches won't offer this ledger." />
            </div>
          ) : (
            <div style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Branch availability (whitelist) can be set after saving — edit the ledger to restrict it to specific branches.
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" value={formData.description} rows={2}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            <IconSystem.check size={16} />
            {mode === 'edit' ? 'Save Changes' : mode === 'copy' ? 'Create Copy' : 'Create Ledger'}
          </button>
        </div>
      </div>

      <ConfirmDialog open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={mode === 'edit' ? `Save changes to "${formData.ledgerName}"?` : mode === 'copy' ? `Create ledger as "${formData.ledgerName}"?` : `Create ledger "${formData.ledgerName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'} cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmSave} onCancel={() => setSaveConfirmOpen(false)} />

      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />

      <ConfirmDialog open={concurrencyConflict.open} title="Record Modified by Another User"
        message={concurrencyConflict.message || 'This record was modified by another user while your form was open. Refresh to load their changes, or keep your current values.'}
        confirmLabel={isRefreshing ? 'Refreshing...' : 'Refresh from Server'} cancelLabel="Keep My Changes" variant="default"
        loading={isRefreshing} onConfirm={handleRefreshFromConflict} onCancel={() => setConcurrencyConflict({ open: false, message: '' })} />
    </>
  );
};

// ============================================================================
// VIEW MODAL
// ============================================================================

export interface AccountLedgerViewModalProps {
  isOpen: boolean;
  ledger: CoaLedgerDto | null;
  onClose: () => void;
  onEdit?: (l: CoaLedgerDto) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (ledgerCode: string) => void;
}

const ViewField: React.FC<{ label: string; value?: string | number | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px' }}>
      {value !== null && value !== undefined && value !== '' ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

export const AccountLedgerViewModal: React.FC<AccountLedgerViewModalProps> = ({
  isOpen, ledger, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !ledger) return;
    minimize({
      id: `${tabId}-view-modal-${ledger.ledgerCode}`,
      title: `View - ${ledger.ledgerCode}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(ledger.ledgerCode),
    });
    onClose();
  }, [tabId, moduleName, ledger, minimize, onClose, onRestoreView]);

  if (!isOpen || !ledger) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Ledger Account Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar"><IconSystem.chevronDown size={16} /></button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Code" value={ledger.ledgerCode} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px', display: 'flex', gap: '0.5rem' }}>
                <span className="badge" style={ledger.statusColor ? { background: ledger.statusColor, color: '#fff' } : undefined}>{ledger.statusName || 'Active'}</span>
                <span className="badge">{ledger.isCustom ? 'Custom (local)' : 'Template'}</span>
              </div>
            </div>
            <ViewField label="Ledger Name" value={ledger.ledgerName} fullWidth />
            <ViewField label="Description" value={ledger.description} fullWidth />
            <ViewField label="Account Type" value={ledger.accountTypeName} />
            <ViewField label="Report Class" value={ledger.reportClassName} />
            <ViewField label="Subtype" value={ledger.subtypeName} />
            <ViewField label="Group" value={ledger.groupName} />
            <ViewField label="Normal Balance" value={ledger.normalBalance} />
            <ViewField label="Kind" value={ledger.ledgerKind} />
            <ViewField label="Nature" value={ledger.ledgerNature} />
            <ViewField label="Base Currency" value={ledger.currencyCode} />
            <ViewField label="Open-item Managed" value={ledger.isOpenItemManaged ? 'Yes' : 'No'} />
            <ViewField label="Reconcilable" value={ledger.isReconcilable ? 'Yes' : 'No'} />
            <ViewField label="Modules" value={(ledger.moduleTags ?? ledger.moduleCodesList ?? []).join(', ')} />
            <ViewField label="Created By" value={ledger.createdByName || '—'} />
            <ViewField label="Created At" value={ledger.createdAt ? new Date(ledger.createdAt).toLocaleString() : null} />
            {ledger.updatedAt && (
              <>
                <ViewField label="Updated By" value={ledger.updatedByName || '—'} />
                <ViewField label="Updated At" value={new Date(ledger.updatedAt).toLocaleString()} />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(ledger); }}>
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default AccountLedgerFormModal;
